import {
  Component,
  JSX,
  Show,
  createEffect,
  onCleanup,
  onMount,
} from 'solid-js';
import { useLogger } from '@trimble/solidjs-utils';
import type { AlertVariant } from './alert.utils';
import {
  ALERT_CSS,
  ALERT_STYLE_ID,
  TC_ICON_PREFIX,
  ALERT_COMPACT_CLASS,
  ALERT_CONTAINER_CLASS,
} from './alert.utils';

const REQUIRED_CUSTOM_ELEMENTS = ['modus-wc-alert'] as const;

/**
 * At least one of `title` or `description` must be present as a prop.
 * Both accept `string | undefined` so reactive accessors (e.g.
 * `signal()?.message`) can be passed directly without coercion.
 */
export type AlertContentProps =
  | {
      readonly title: string | undefined;
      readonly description?: string;
    }
  | { readonly title?: undefined; readonly description: string | undefined };

interface AlertBaseProps {
  /**
   * Icon to display. Accepts three forms:
   * - **string with `tc-icon-` prefix** → Trimble Connect font icon (rendered via content slot).
   * - **plain string** (no prefix) → Modus icon name (forwarded to native `icon` attribute).
   * - **JSX element** → rendered directly inside the content slot.
   */
  readonly icon?: JSX.Element | string;
  /** Modus alert colour variant (maps to `modus-wc-alert` `variant`). `undefined` renders the gray/neutral base. */
  readonly alertVariant?: AlertVariant;
  /** Whether the user can dismiss the alert via a close button. */
  readonly dismissible: boolean;
  /** Extra CSS class(es) forwarded to `modus-wc-alert` `custom-class`. */
  readonly alertCustomClass?: string;
  /** When true, applies the compact layout class for reduced vertical padding. */
  readonly compact?: boolean;
  /**
   * Optional JSX projected into `modus-wc-alert`'s `button` slot via a
   * wrapper `<div slot="button">`. Can be any element — a Modus button,
   * a Solid component, or a native `<button>`.
   */
  readonly customButton?: JSX.Element;
  /**
   * When `true`, clicking anywhere on the custom-button wrapper fires
   * `onDismiss`, dismissing the alert. Defaults to `false` so the caller
   * retains full control. Toast sets this to `true` internally so that
   * action buttons automatically trigger the fade-out/close flow.
   */
  readonly customButtonDismiss?: boolean;
  /**
   * Auto-dismiss delay in milliseconds. When set, `onDismiss` is called after
   * this duration. Intended for standalone Alert usage.
   *
   * **Toast consumers:** do not pass `delayTime` here when Alert is rendered
   * inside Toast. Toast orchestrates its own two-phase timer (fade-start then
   * close) so the total visible time equals the caller's `delayTime`. Passing
   * it to Alert would create a duplicate timer with no animation awareness.
   *
   * Implementation note: `modus-wc-alert` has a native `delay` attribute,
   * but it calls `this.el.remove()` after emitting `dismissClick`, which
   * would break SolidJS DOM ownership. We manage the timer ourselves to let
   * the parent control unmounting via reactive state.
   */
  readonly delayTime?: number;
  /** Callback invoked when the user clicks the dismiss button, presses Escape, or the delay expires. */
  onDismiss?(): void;
}

export type AlertProps = AlertBaseProps & AlertContentProps;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- modus-wc-alert defaults variant to 'info' internally; passing '' overrides that so the gray/neutral base CSS applies, but Stencil's generated types don't include '' in the union
const variantForWc = (v: AlertVariant | undefined): any => v ?? '';

const isTcIcon = (icon: JSX.Element | string | undefined): icon is string =>
  typeof icon === 'string' && icon.startsWith(TC_ICON_PREFIX);

const isModusIcon = (icon: JSX.Element | string | undefined): icon is string =>
  typeof icon === 'string' && !icon.startsWith(TC_ICON_PREFIX);

const renderTcIcon = (name: string): JSX.Element => (
  <modus-wc-icon custom-class={`icon-font ${name} i24`} />
);

/**
 * Alert component wrapping Modus `modus-wc-alert` with custom icon support.
 *
 * Two rendering paths:
 * 1. **Modus icon** (plain string without `tc-icon-` prefix): Uses `modus-wc-alert`'s native
 *    `icon`, `alert-title`, and `alert-description` attributes.
 * 2. **Custom icon** (string with `tc-icon-` prefix, or JSX element): Omits title/description
 *    attributes to activate the `content` slot, then renders a flex layout with the custom
 *    icon alongside the title and description text. The default leading icon is hidden via CSS.
 */
export const Alert: Component<AlertProps> = (props) => {
  const logger = useLogger().getLogger('Alert');
  let containerRef!: HTMLDivElement;

  onMount(() => {
    if (!document.getElementById(ALERT_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = ALERT_STYLE_ID;
      style.textContent = ALERT_CSS;
      document.head.appendChild(style);
    }

    const missing = REQUIRED_CUSTOM_ELEMENTS.filter(
      (tag) => !customElements.get(tag)
    );
    if (missing.length > 0) {
      logger.warn(
        `Modus Web Components not registered: ${missing.join(', ')}. ` +
          'Ensure @trimble-oss/moduswebcomponents is loaded (and modus-icons.css is imported) before rendering Alert.'
      );
    }

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && props.dismissible) {
        props.onDismiss?.();
      }
    };
    document.addEventListener('keydown', handleEscape);
    onCleanup(() => document.removeEventListener('keydown', handleEscape));

    // Suppress the native modus-wc-alert Escape keyup handler which calls
    // this.el.remove() — intercepted during capture phase so it never
    // reaches the web component's own listener.
    const suppressNativeEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
      }
    };
    containerRef.addEventListener('keyup', suppressNativeEscape, true);
    onCleanup(() =>
      containerRef.removeEventListener('keyup', suppressNativeEscape, true)
    );
  });

  createEffect(() => {
    const delay = props.delayTime;
    if (!delay || delay <= 0) {
      return;
    }
    const timerId = setTimeout(() => props.onDismiss?.(), delay);
    onCleanup(() => clearTimeout(timerId));
  });

  const isCustom = (): boolean =>
    props.icon !== undefined && !isModusIcon(props.icon);

  const customClass = (): string =>
    [
      props.alertCustomClass,
      isCustom() && 'has-custom-icon',
      props.compact && ALERT_COMPACT_CLASS,
    ]
      .filter(Boolean)
      .join(' ');

  const renderCustomIcon = (): JSX.Element | null => {
    if (!props.icon) return null;
    if (isTcIcon(props.icon)) return renderTcIcon(props.icon);
    return props.icon as JSX.Element;
  };

  const renderCustomButton = (): JSX.Element | null => {
    if (!props.customButton) return null;
    return (
      <div
        slot="button"
        class="alert-custom-button"
        onClick={() => {
          if (props.customButtonDismiss) {
            props.onDismiss?.();
          }
        }}
      >
        {props.customButton}
      </div>
    );
  };

  // Title and description are always rendered inside `slot="content"` so a
  // single stable <modus-wc-alert> instance is reused across all icon/title/
  // button prop changes. Modus icon names are still forwarded via the native
  // `icon` attribute (rendered as the variant-coloured leading icon); tc-icon
  // strings and JSX icons render inline inside the content slot.
  return (
    <div ref={containerRef} class={ALERT_CONTAINER_CLASS}>
      <modus-wc-alert
        variant={variantForWc(props.alertVariant)}
        dismissible={props.dismissible}
        icon={isModusIcon(props.icon) ? props.icon : undefined}
        custom-class={customClass()}
        on:dismissClick={() => props.onDismiss?.()}
      >
        <div
          slot="content"
          class="alert-custom-content"
          style={{
            display: 'flex',
            // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSSProperties uses kebab-case, not camelCase
            'align-items': 'center',
            gap: 'var(--modus-wc-spacing-sm, 0.5rem)',
          }}
        >
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          <Show when={isCustom()}>{renderCustomIcon()}</Show>
          <div>
            <Show when={props.title}>
              <div
                class="title"
                style={{
                  // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSSProperties uses kebab-case, not camelCase
                  'font-size': 'var(--modus-wc-font-size-md, 0.875rem)',
                  // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSSProperties uses kebab-case, not camelCase
                  'font-weight': 'var(--modus-wc-font-weight-bold, 700)',
                }}
              >
                {props.title}
              </div>
            </Show>
            <Show when={props.description}>
              <div
                class="description"
                style={{
                  // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSSProperties uses kebab-case, not camelCase
                  'font-size': 'var(--modus-wc-font-size-md, 0.875rem)',
                  // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSSProperties uses kebab-case, not camelCase
                  'overflow-wrap': 'break-word',
                  // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSSProperties uses kebab-case, not camelCase
                  'white-space': 'normal',
                }}
              >
                {props.description}
              </div>
            </Show>
          </div>
        </div>
        {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
        {renderCustomButton()}
      </modus-wc-alert>
    </div>
  );
};
